import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTranslate } from '@tolgee/react';
import { AwsPdfResponse } from '../AwsGraphs';
import { formatNumber } from 'utils/formatNumber';

/**
 * Props for ProjectedSavingsLine component.
 */
export interface ProjectedSavingsLineProps {
    data: AwsPdfResponse;
}

/**
 * Renders a line chart showing projected savings over time.
 * @param {ProjectedSavingsLineProps} props - The props for the component.
 */
const ProjectedSavingsLine: React.FC<ProjectedSavingsLineProps> = ({ data }) => {
    const { t } = useTranslate();
    const options: ApexOptions = {
        chart: {
            type: 'line',
            fontFamily: 'inherit',
            toolbar: { show: false }
        },
        colors: ['#0003c9', '#ef11e9'],
        stroke: { width: 3 },
        xaxis: {
            categories: data.data_overtime?.with_frust.map((_, i) => `${i + 1}`) || [],
            title: { text: t('pdf_success.charts.overtime.x') }
        },
        yaxis: {
            labels: {
                formatter: function (val: number) {
                    return formatNumber(val);
                }
            },
            title: { text: t('pdf_success.charts.overtime.y') }
        },
        legend: {
            position: 'top',
            fontSize: '14px',
            horizontalAlign: 'center',
            itemMargin: { horizontal: 8, vertical: 5 }
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (val: number) => `$${formatNumber(val)}`
            }
        },
        dataLabels: { enabled: false },
        grid: { strokeDashArray: 4 }
    };
    const series = [
        {
            name: t('pdf_success.charts.overtime.with_frust'),
            data: data.data_overtime?.with_frust || []
        },
        {
            name: t('pdf_success.charts.overtime.without_frust'),
            data: data.data_overtime?.without_frust || []
        }
    ];
    return (
        <Box sx={{ width: '100%', display: 'flex' }}>
            <Card
                sx={{
                    width: '100%',
                    minHeight: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2.5px solid #000000',
                    filter: 'drop-shadow(8px 8px 0 black)',
                    borderRadius: '.5rem',
                    minWidth: 300,
                    mb: 2
                }}
            >
                <CardContent
                    sx={{
                        pt: 2,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            {t('pdf_success.charts.overtime.title')}
                        </Typography>
                        <Typography variant="h5">{t('pdf_success.charts.overtime.description')}</Typography>
                    </Box>
                    <Chart options={options} series={series} type="line" height={300} />
                </CardContent>
            </Card>
        </Box>
    );
};

export default ProjectedSavingsLine;
